import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { selectLabels } from '../../redux/configuration/selectors'
import { setReturnCrossing } from '../../redux/activeCrossings/actions'
import * as UserSelectionActions from '../../redux/userSelections/actions'
import { ENABLE_EXTRAS, ModifyFlowType } from '../../configuration/constants'
import appRoutes from '../../configuration/appRoutes'
import { selectReturnCrossing } from '../../redux/crossings/selectors'
import {
  selectReturnDate,
  selectReturnRoute
} from '../../redux/ferryRouteSelections/selectors'
import { hasSetPetPassengers } from '../../redux/passengerSelections/selectors'
import {
  selectActiveDepartureCrossing,
  selectActiveReturnCrossing
} from '../../redux/activeCrossings/selectors'
import { fetchCrossing } from '../../redux/crossings/actions'
import dateFormats from '../../configuration/dateFormats'
import {
  selectActiveModifyBooking,
  selectModifyFlowType
} from '../../redux/modifyBooking/selectors'
import { selectReturnVehicle } from '../../redux/vehicleSelections/selectors'
import RouteSelection from '../../components/RouteSelection/RouteSelection'
import HighlightedCrossingRouteSelection from '../../components/RouteSelection/HighlightedCrossingRouteSelection'

const ReturnRouteSelection = ({ handleRouteChange }) => {
  const labels = useSelector(selectLabels)
  const dispatch = useDispatch()

  const crossingsData = useSelector(selectReturnCrossing)
  const departureDate = useSelector(selectReturnDate)
  const activeDepartureCrossing = useSelector(selectActiveDepartureCrossing)
  const returnRoute = useSelector(selectReturnRoute)
  const currentActiveReturn = useSelector(selectActiveReturnCrossing)
  const hasPets = useSelector(hasSetPetPassengers)

  const activeModifyBooking = useSelector(selectActiveModifyBooking)
  const modifyFlowType = useSelector(selectModifyFlowType)

  const activeVehicle = useSelector(selectReturnVehicle)

  const activeVehicleResourceCode = activeVehicle?.vehicleType?.resourceCode
  useEffect(() => {
    if (modifyFlowType === ModifyFlowType.DEPARTURE_LOCKED) {
      dispatch(
        fetchCrossing('departure', {
          setActiveCrossingFromModification: Boolean(
            activeModifyBooking?.departureRoute
          ),
          activeModifyBooking: activeModifyBooking?.departureRoute
        })
      )
    }
    dispatch(
      fetchCrossing('return', {
        setActiveCrossingFromModification: Boolean(
          activeModifyBooking?.returnRoute
        ),
        activeModifyBooking: activeModifyBooking?.returnRoute
      })
    )
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isLoading = !crossingsData || crossingsData.loading
  const error = crossingsData && crossingsData.error

  const departureCrossingArrivalDate = activeDepartureCrossing
    ? activeDepartureCrossing.arrivalDate
    : null
  const departureCrossingArrivalTime = activeDepartureCrossing
    ? activeDepartureCrossing.arrivalTime
    : null

  const filteredCrossings =
    crossingsData && crossingsData.crossings
      ? Object.entries(crossingsData.crossings).reduce(
          (obj, [dayKey, crossings]) => {
            // skip any days before our departure crossing.
            // we shouldn't have any, but just to be safe.
            let filteredDayCrossings

            if (
              moment(dayKey, dateFormats.default).isBefore(
                departureCrossingArrivalDate,
                'day'
              )
            ) {
              return obj
            }

            // filter out any crossings on the same day that depart after our departure crossing arrives.
            if (
              moment(dayKey, dateFormats.default).isSame(
                departureCrossingArrivalDate,
                'day'
              )
            ) {
              filteredDayCrossings = crossings.filter(crossing =>
                moment(crossing.departureTime, dateFormats.timeOnly).isAfter(
                  moment(departureCrossingArrivalTime, dateFormats.timeOnly),
                  'minutes'
                )
              )
            } else {
              // these are all on a later date than our departure crossing
              filteredDayCrossings = crossings
            }

            if (filteredDayCrossings.length) {
              obj[dayKey] = filteredDayCrossings
            }

            return obj
          },
          {}
        )
      : null

  let continueBtnLabel, continueBtnRoute
  /* if (ENABLE_ACCOMMODATIONS) {
    continueBtnLabel = labels.continueToAccommodationsBtn
    continueBtnRoute = appRoutes.accommodations.pathname
  } else */ if (
    ENABLE_EXTRAS &&
    (returnRoute.ferryId === 'nfl' || returnRoute.ferryId === 'bfl') &&
    hasPets
  ) {
    continueBtnLabel = labels.continueToExtrasBtn
    continueBtnRoute = appRoutes.extras.pathname
  } else {
    continueBtnLabel = labels.continueToSummaryBtn
    continueBtnRoute = appRoutes.summary.pathname
  }

  const handleCrossingSelection = crossing => {
    const { departureDate, departureTime } = crossing
    dispatch(
      UserSelectionActions.setReturnCrossing({
        date: moment(departureDate).format('YYYY-MM-DD'),
        time: departureTime
      })
    )
    dispatch(setReturnCrossing(crossing)) // TODO: Deprecate
  }

  if (
    process.env.REACT_APP_CROSSING_SELECTION_COMPONENT ===
    'highlight_selected_dates'
  ) {
    return (
      <HighlightedCrossingRouteSelection
        crossingsData={crossingsData}
        crossings={filteredCrossings}
        departureDate={departureDate}
        route={returnRoute}
        selectCrossing={handleCrossingSelection}
        currentSelection={currentActiveReturn}
        continueButtonText={continueBtnLabel}
        continueIsDisabled={
          !currentActiveReturn ||
          !crossingsData?.crossings ||
          Object.keys(crossingsData.crossings).length === 0
        }
        continueClickHandler={() => handleRouteChange(continueBtnRoute)}
        isLoading={isLoading}
        error={error}
        activeVehicleResourceCode={activeVehicleResourceCode}
        activeVehicle={activeVehicle}
        titleLabel={labels.return}
        newRoutesClickHandler={() => {
          handleRouteChange(appRoutes.ferryRoutes)
        }}
      />
    )
  }

  return (
    <RouteSelection
      crossingsData={crossingsData}
      crossings={filteredCrossings}
      departureDate={departureDate}
      route={returnRoute}
      selectCrossing={handleCrossingSelection}
      currentSelection={currentActiveReturn}
      continueButtonText={continueBtnLabel}
      continueIsDisabled={
        !currentActiveReturn ||
        !crossingsData?.crossings ||
        Object.keys(crossingsData.crossings).length === 0
      }
      continueClickHandler={() => handleRouteChange(continueBtnRoute)}
      isLoading={isLoading}
      error={error}
      activeVehicleResourceCode={activeVehicleResourceCode}
      activeVehicle={activeVehicle}
      titleLabel={labels.return}
    />
  )
}

export default ReturnRouteSelection
